import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Clock, ArrowRight, ArrowLeft, BookOpen, AlertTriangle } from "lucide-react";
import Seo from "@/components/Seo";
import { Eyebrow } from "@/components/ui-bits";
import { publicApi, api } from "@/lib/api";
import { PRODUCTS, INVERTER_PRODUCTS, PANEL_PRODUCTS, COMPANY } from "@/data/site";

const SITE = "https://eibattery.com.au";
const ALL_PRODUCTS = [...PRODUCTS, ...INVERTER_PRODUCTS, ...PANEL_PRODUCTS];

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "";

const markdownComponents = {
  h1: ({ node, ...props }) => <h2 className="mt-10 font-display text-2xl font-bold text-ei-navy" {...props} />,
  h2: ({ node, ...props }) => <h2 className="mt-10 font-display text-xl font-bold text-ei-navy" {...props} />,
  h3: ({ node, ...props }) => <h3 className="mt-8 font-display text-base md:text-lg font-bold text-ei-navy" {...props} />,
  p: ({ node, ...props }) => <p className="mt-4 text-base text-ei-graphite/90 leading-relaxed" {...props} />,
  ul: ({ node, ...props }) => <ul className="mt-4 space-y-2 list-disc pl-5 text-ei-graphite/90" {...props} />,
  ol: ({ node, ...props }) => <ol className="mt-4 space-y-2 list-decimal pl-5 text-ei-graphite/90" {...props} />,
  li: ({ node, ...props }) => <li className="text-base leading-relaxed" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="mt-6 border-l-2 border-ei-gold pl-4 text-ei-graphite/75 italic" {...props} />
  ),
  a: ({ node, ...props }) => <a className="text-ei-navy underline underline-offset-2 hover:text-ei-golddark" {...props} />,
  table: ({ node, ...props }) => (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full text-sm border border-ei-mist" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th className="bg-ei-mist/40 text-left font-display text-xs font-bold text-ei-navy px-3 py-2 border border-ei-mist" {...props} />,
  td: ({ node, ...props }) => <td className="px-3 py-2 border border-ei-mist align-top" {...props} />,
  img: ({ node, ...props }) => <img className="mt-6 rounded-md border border-ei-mist" loading="lazy" {...props} alt={props.alt || ""} />,
};

export default function GuideArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const previewId = params.get("preview");
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let live = true;
    setState("loading");
    const request = previewId ? api.get(`/staff/guides/${previewId}`) : publicApi.get(`/guides/${slug}`);
    request
      .then((r) => {
        if (!live) return;
        if (previewId) {
          setArticle(r.data);
          setRelated([]);
        } else if (r.data.redirect_to) {
          navigate(`/guides/${r.data.redirect_to}`, { replace: true });
          return;
        } else {
          setArticle(r.data.article);
          setRelated(r.data.related || []);
        }
        setState("ready");
      })
      .catch(() => live && setState("missing"));
    return () => {
      live = false;
    };
  }, [slug, previewId, navigate]);

  const products = useMemo(
    () => (article?.product_slugs || []).map((s) => ALL_PRODUCTS.find((p) => p.slug === s)).filter(Boolean),
    [article]
  );

  const jsonLd = useMemo(() => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: article.seo_title || article.title,
          description: article.meta_description || article.excerpt,
          image: article.image ? [article.image.startsWith("http") ? article.image : `${SITE}${article.image}`] : undefined,
          datePublished: article.published_at || undefined,
          dateModified: article.updated_at || article.published_at || undefined,
          author: { "@type": "Organization", name: article.author || COMPANY.name },
          publisher: { "@type": "Organization", name: COMPANY.name, url: SITE },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/guides/${article.slug}` },
          articleSection: article.category || undefined,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "EI Battery Guides", item: `${SITE}/guides` },
            { "@type": "ListItem", position: 3, name: article.title, item: `${SITE}/guides/${article.slug}` },
          ],
        },
      ],
    };
  }, [article]);

  if (state === "loading") {
    return (
      <div className="py-32 flex justify-center" data-testid="guide-loading">
        <Loader2 className="w-6 h-6 animate-spin text-ei-navy/40" />
      </div>
    );
  }

  if (state === "missing" || !article) {
    return (
      <>
        <Seo title="Guide not found — EI Battery Solutions" description="This guide is no longer available." noindex />
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="guide-not-found">
          <BookOpen className="w-7 h-7 mx-auto text-ei-golddark" />
          <h1 className="mt-5 font-display text-2xl font-bold text-ei-navy">We couldn't find that guide</h1>
          <p className="mt-3 text-sm text-ei-graphite/75">It may have been renamed or withdrawn.</p>
          <Link
            to="/guides"
            className="mt-7 inline-flex items-center gap-2 bg-ei-navy text-ei-white font-display text-sm font-bold px-6 py-3 rounded-sm hover:bg-ei-graphite transition-colors min-h-[48px]"
            data-testid="guide-back-to-index"
          >
            <ArrowLeft className="w-4 h-4" /> All guides
          </Link>
        </section>
      </>
    );
  }

  const isPreview = Boolean(previewId);
  const canonical = `${SITE}/guides/${article.slug}`;

  return (
    <>
      <Seo
        title={article.seo_title || `${article.title} — EI Battery Guides`}
        description={article.meta_description || article.excerpt}
        canonical={canonical}
        image={article.image ? (article.image.startsWith("http") ? article.image : `${SITE}${article.image}`) : ""}
        imageAlt={article.image_alt}
        type="article"
        publishedAt={article.published_at}
        updatedAt={article.updated_at}
        author={article.author}
        noindex={isPreview || article.status !== "published"}
        jsonLd={isPreview ? null : jsonLd}
      />

      {isPreview && (
        <div className="bg-ei-gold text-ei-navy" data-testid="guide-preview-banner">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 text-sm font-display font-bold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Staff preview — this {article.status} is hidden from customers and search engines.
          </div>
        </div>
      )}

      <article className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <nav className="text-xs text-ei-graphite/60" aria-label="Breadcrumb" data-testid="guide-breadcrumb">
          <Link to="/" className="hover:text-ei-navy">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/guides" className="hover:text-ei-navy">Guides</Link>
          <span className="mx-2">/</span>
          <span className="text-ei-graphite/80">{article.title}</span>
        </nav>

        {article.category && <Eyebrow className="mt-6 text-ei-golddark">{article.category}</Eyebrow>}
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-ei-navy leading-tight" data-testid="guide-title">
          {article.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ei-graphite/60">
          <span>{article.author}</span>
          {article.published_at && <span>{fmtDate(article.published_at)}</span>}
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {article.reading_minutes} min read
          </span>
        </div>
        {article.excerpt && <p className="mt-6 text-base md:text-lg text-ei-graphite/85 leading-relaxed">{article.excerpt}</p>}

        {article.image && (
          <figure className="mt-8">
            <img
              src={article.image}
              alt={article.image_alt || article.title}
              className="w-full rounded-md border border-ei-mist bg-white object-contain"
              data-testid="guide-hero-image"
            />
            {article.image_alt && <figcaption className="mt-2 text-xs text-ei-graphite/55">{article.image_alt}</figcaption>}
          </figure>
        )}

        <div className="mt-8" data-testid="guide-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {article.body}
          </ReactMarkdown>
        </div>

        {article.sources?.length > 0 && (
          <section className="mt-12 border-t border-ei-mist pt-6" data-testid="guide-sources">
            <h2 className="font-display text-sm font-bold text-ei-navy">Source references</h2>
            <ul className="mt-3 space-y-1 text-sm text-ei-graphite/75">
              {article.sources.map((source) => (
                <li key={source} className="break-words">{source}</li>
              ))}
            </ul>
          </section>
        )}

        {products.length > 0 && (
          <section className="mt-12" data-testid="guide-products">
            <h2 className="font-display text-lg font-bold text-ei-navy">Products mentioned</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {products.map((product) => (
                <Link
                  key={product.slug}
                  to={`/products/${product.slug}`}
                  className="flex items-center gap-4 bg-white border border-ei-mist rounded-md p-4 hover:border-ei-navy/40 transition-colors"
                  data-testid={`guide-product-${product.slug}`}
                >
                  {product.image && <img src={product.image} alt={product.name} className="w-16 h-16 object-contain" loading="lazy" />}
                  <div className="min-w-0">
                    <div className="font-display text-sm font-bold text-ei-navy">{product.name}</div>
                    {product.manufacturer && <div className="text-xs text-ei-graphite/60">{product.manufacturer}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12 bg-ei-navy text-ei-white rounded-md p-8 flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
          <div>
            <h2 className="font-display text-lg md:text-xl font-bold">Ready to price this for a job?</h2>
            <p className="mt-2 text-sm text-ei-white/70 max-w-xl leading-relaxed">
              Send the site details and we'll come back with equipment, freight and lead times.
            </p>
          </div>
          <Link
            to="/request-a-quote"
            className="inline-flex items-center justify-center gap-2 bg-ei-gold text-ei-navy font-display text-sm font-bold px-6 py-3 rounded-sm hover:bg-ei-white transition-colors min-h-[48px]"
            data-testid="guide-enquiry-cta"
          >
            Request a quote <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {related.length > 0 && (
          <section className="mt-14" data-testid="guide-related">
            <h2 className="font-display text-lg font-bold text-ei-navy">Related guides</h2>
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              {related.map((item) => (
                <Link
                  key={item.slug}
                  to={`/guides/${item.slug}`}
                  className="bg-white border border-ei-mist rounded-md p-4 hover:border-ei-navy/40 transition-colors"
                  data-testid={`guide-related-${item.slug}`}
                >
                  {item.category && <div className="text-xs text-ei-golddark font-display font-bold">{item.category}</div>}
                  <div className="mt-1 font-display text-sm font-bold text-ei-navy leading-snug">{item.title}</div>
                  <div className="mt-2 text-xs text-ei-graphite/65 line-clamp-3">{item.excerpt}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}
